import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Space, Tooltip, message } from 'antd';
import { EyeOutlined, FileExcelOutlined, FilePdfOutlined, HistoryOutlined, MailOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';
import { useAuthStore } from '@/store/authStore';
import { openBlobInNewTab, triggerDownload } from '@/reports/utils/exportTable';
import {
  getReportDocument,
  getReportDocumentFile,
  reportFileName,
  sendReportDocument,
  type ReportDocument,
  type ReportParams,
} from '@/services/reportDocuments.service';
import SendReportModal from './SendReportModal';
import ReportSendHistory from './ReportSendHistory';

interface Props {
  reportKey: string;
  /** Params the server builder receives; null disables every action. */
  params: ReportParams | null;
  /** Loaded document; supplies email defaults and file names. */
  document?: ReportDocument | null;
  emailLabel?: string;
  defaultMessage?: string;
  size?: 'small' | 'middle';
  /** Any one of these shows the Email button; must match the definition's sendPermissions on the server. */
  sendPermissions?: string[];
  /** Single-document definitions (quotation, purchase order): history shows sends of this document only. */
  documentId?: string;
  /** Offers a "Preprinted stationery" PDF without the letterhead header and footer. Email always uses the letterhead. */
  allowPreprinted?: boolean;
}

const DEFAULT_SEND_PERMISSIONS = ['finance.reports.send'];
const PREPRINTED_STORAGE_KEY = 'reportDocument.preprinted';

function readPreprintedPreference(): boolean {
  try {
    return window.localStorage.getItem(PREPRINTED_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * Standard toolbar for every server-rendered document (financial reports, quotations, estimations,
 * purchase orders): letterhead PDF (preview/download), xlsx export, email with PDF attached, and the
 * send history. Screen, PDF and Excel come from the same build.
 */
const ReportDocumentActions: React.FC<Props> = ({
  reportKey,
  params: rawParams,
  document: loadedDocument,
  emailLabel,
  defaultMessage,
  size = 'middle',
  sendPermissions = DEFAULT_SEND_PERMISSIONS,
  documentId,
  allowPreprinted = false,
}) => {
  const { t } = useTranslation('common');
  const resolvedEmailLabel = emailLabel ?? t('email');
  const user = useAuthStore((s) => s.user);
  const { preferences } = useUserPreferences();
  const params = useMemo(() => {
    if (!rawParams) return null;
    return {
      ...rawParams,
      document_language: rawParams.document_language ?? preferences.document_language ?? 'en',
    } as ReportParams;
  }, [rawParams, preferences.document_language]);
  const [preprinted, setPreprinted] = useState(readPreprintedPreference);
  const usePreprinted = allowPreprinted && preprinted;
  const togglePreprinted = (checked: boolean) => {
    setPreprinted(checked);
    try {
      window.localStorage.setItem(PREPRINTED_STORAGE_KEY, checked ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
  };
  const canSend = Boolean(user?.is_super_admin || sendPermissions.some((p) => user?.permissions?.includes(p)));
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState<'preview' | 'pdf' | 'xlsx' | 'email' | null>(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [fetched, setFetched] = useState<{ paramsKey: string; doc: ReportDocument } | null>(null);
  const disabled = !params;
  const paramsKey = params ? JSON.stringify(params) : '';
  const document = loadedDocument ?? (fetched && fetched.paramsKey === paramsKey ? fetched.doc : null);

  const openEmail = async () => {
    if (!params) return;
    if (!document) {
      setBusy('email');
      try {
        setFetched({ paramsKey, doc: await getReportDocument(reportKey, params) });
      } catch (err: any) {
        message.error(err?.response?.data?.message || err?.message || t('couldNotLoadReport'));
        return;
      } finally {
        setBusy(null);
      }
    }
    setEmailOpen(true);
  };

  const run = async (kind: 'preview' | 'pdf' | 'xlsx', preprintedPdf = usePreprinted) => {
    if (!params) return;
    setBusy(kind);
    try {
      const blob = await getReportDocumentFile(reportKey, kind === 'xlsx' ? 'xlsx' : 'pdf', params, { preprinted: preprintedPdf });
      if (kind === 'preview') openBlobInNewTab(blob);
      else triggerDownload(blob, reportFileName(document ?? undefined, reportKey, kind === 'xlsx' ? 'xlsx' : 'pdf'));
    } catch (err: any) {
      message.error(err?.response?.data?.message || err?.message || t('couldNotGenerateFile'));
    } finally {
      setBusy(null);
    }
  };

  const defaults = useMemo(
    () => ({
      to: document?.email.to ?? [],
      cc: [],
      subject: document?.email.subject ?? '',
      message:
        defaultMessage ??
        (document
          ? [
              `Please find attached the ${document.title}${document.reference ? ` (${document.reference})` : ''}.`,
              ...document.email.key_figures.slice(0, 3).map((k) => `${k.label}: ${k.value}`),
            ].join('\n')
          : ''),
    }),
    [document, defaultMessage]
  );

  return (
    <>
      <Space wrap size="small">
        {allowPreprinted && (
          <Tooltip title={t('preprintedStationeryHint')}>
            <Checkbox checked={preprinted} onChange={(e) => togglePreprinted(e.target.checked)}>
              {t('preprintedStationery')}
            </Checkbox>
          </Tooltip>
        )}
        <Button size={size} icon={<EyeOutlined />} disabled={disabled} loading={busy === 'preview'} onClick={() => run('preview')}>
          {t('previewPdf')}
        </Button>
        <Button size={size} icon={<FilePdfOutlined />} disabled={disabled} loading={busy === 'pdf'} onClick={() => run('pdf')}>
          {t('pdf')}
        </Button>
        <Button size={size} icon={<FileExcelOutlined />} disabled={disabled} loading={busy === 'xlsx'} onClick={() => run('xlsx')}>
          {t('excel')}
        </Button>
        {canSend && (
          <Button size={size} type="primary" icon={<MailOutlined />} disabled={disabled} loading={busy === 'email'} onClick={openEmail}>
            {resolvedEmailLabel}
          </Button>
        )}
        <Tooltip title={t('sendHistory')}>
          <Button
            size={size}
            icon={<HistoryOutlined />}
            onClick={() => setHistoryOpen(true)}
            aria-label={t('sendHistory')}
          />
        </Tooltip>
      </Space>
      {canSend && document && params && (
        <SendReportModal
          open={emailOpen}
          title={t('emailTitle', { title: document.title })}
          okText={t('send')}
          defaults={defaults}
          attachmentLabel={t('attachmentWillBeAttached', {
            name: reportFileName(document, reportKey, 'pdf'),
          })}
          recipientHint={document.party && !document.email.to.length ? t('noEmailOnFile') : undefined}
          onPreview={() => run('preview', false)}
          onClose={() => setEmailOpen(false)}
          onSubmit={(v) => sendReportDocument(reportKey, { params, to: v.to, cc: v.cc, subject: v.subject, message: v.message })}
          onSent={() => queryClient.invalidateQueries({ queryKey: ['report-dispatches'] })}
        />
      )}
      <ReportSendHistory
        open={historyOpen}
        reportKey={reportKey}
        partyType={document?.party?.type}
        partyId={document?.party?.id ?? undefined}
        documentId={documentId}
        onClose={() => setHistoryOpen(false)}
      />
    </>
  );
};

export default ReportDocumentActions;
